import { Style } from "@/types/styles";
import { LitElement } from "lit";
import { property } from "lit/decorators.js";

export abstract class BaseElement extends LitElement {
  @property({ type: Boolean }) shadow = false;

  constructor() {
    super();
  }
  protected parseJsonAttribute<T = unknown>(attrName: string): T {
    try {
      const raw = this.getAttribute(attrName);
      return raw ? JSON.parse(raw) : ({} as T);
    } catch {
      return {} as T;
    }
  }

  protected get sbStyle(): Style {
    return this.parseJsonAttribute<Style>("sb-style");
  }
  protected getAttributesToExclude(): string[] {
    return [];
  }

  // Filter attributes based on exclusion rules
  protected getFilteredAttributes(): Record<string, string> {
    const attributes: Record<string, string> = {};
    Array.from(this.attributes).forEach((attr) => {
      if (!this.getAttributesToExclude().includes(attr.name)) {
        attributes[attr.name] = attr.value ?? "";
      }
    });
    return attributes;
  }

  protected isCustomPropertySet(property: string): boolean {
    const value = getComputedStyle(this).getPropertyValue(property).trim();
    return value !== "inherit" && value.length > 0;
  }

  protected applyCustomClass(
    variables: string[],
    selector: string,
    className = "",
  ): void {
    if (variables.some((variable) => this.isCustomPropertySet(variable))) {
      this.shadowRoot?.querySelector(selector)?.classList.add(className);
    }
  }

  protected moveAttributesToElement(
    target: HTMLElement,
    attributesToMove: string[] = ["id", "class"],
  ): void {
    if (!target) return;

    attributesToMove.forEach((attr) => {
      const value = this.getAttribute(attr);

      if (value !== null) {
        target.setAttribute(attr, value);
        this.removeAttribute(attr);
      }
    });
  }

  protected removeAttributesFromHost(
    attributes: string[] = ["id", "class"],
  ): void {
    attributes.forEach((attr) => {
      if (this.hasAttribute(attr)) {
        this.removeAttribute(attr);
      }
    });
  }

  private __initialAttributesCache: Record<string, string> | null = null;

  protected captureHostAttributes(): Record<string, string> {
    if (this.__initialAttributesCache) return this.__initialAttributesCache;

    const attrs: Record<string, string> = {};

    Array.from(this.attributes).forEach((attr) => {
      attrs[attr.name] = attr.value;
    });

    this.__initialAttributesCache = attrs;
    return attrs;
  }

  protected getHostClass(): string {
    return this.captureHostAttributes()["class"] ?? "";
  }

  protected removeDuplicateContent() {
    const walker = document.createTreeWalker(
      this,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          if (node.nodeType === Node.COMMENT_NODE)
            return NodeFilter.FILTER_REJECT;
          if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
            return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      },
    );

    const seen = new Map<string, Node>();
    const nodesToRemove: Node[] = [];

    let node: Node | null;
    while ((node = walker.nextNode())) {
      let key: string;
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (!text) continue;
        key = `text:${text}`;
      } else if (node instanceof HTMLElement) {
        const attributes = Array.from(node.attributes)
          .map((attr) => `${attr.name}=${attr.value}`)
          .sort()
          .join(":");
        key = `element:${node.tagName}:${attributes}`;
      } else {
        continue;
      }

      if (seen.has(key)) {
        const firstNode = seen.get(key);
        if (firstNode?.parentNode) nodesToRemove.push(firstNode);
        seen.set(key, node);
      } else {
        seen.set(key, node);
      }
    }

    nodesToRemove.forEach((node) => node.parentNode?.removeChild(node));
  }

  protected moveLightDomChildrenInto(
    target: HTMLElement,
    specificNodes?: Node[],
  ): void {
    let nodesToMove: Node[];

    if (specificNodes?.length) {
      nodesToMove = specificNodes.filter(
        (node) =>
          this.contains(node) && node !== target && !node.contains(target),
      );

      // Remove existing duplicates in target
      nodesToMove.forEach((node) => {
        const existing = Array.from(target.childNodes).find((n) =>
          n.isEqualNode?.(node),
        );
        if (existing) target.removeChild(existing);
      });
    } else {
      nodesToMove = Array.from(this.childNodes).filter(
        (node) =>
          node !== target && !target.contains(node) && !node.contains(target),
      );
    }

    nodesToMove.forEach((node) => target.appendChild(node));
  }
}
